import { useMemo } from 'react'
import type { WidgetConfiguration } from '../types'

export function useEmbedCode(config: WidgetConfiguration | null, tenantSlug: string, kbId?: string) {
  const { embedCode, embedCodeSingleScript, widgetScriptUrl, singleScriptUrl } = useMemo(() => {
    if (!config || !tenantSlug) return { 
      embedCode: '', 
      embedCodeSingleScript: '',
      widgetScriptUrl: '', 
      singleScriptUrl: '' 
    }

    // Use environment variable for API base URL (configured per environment)
    // Development: http://localhost:8000 or http://api.localhost:8000
    // Production: https://api.devfctrz.com
    // Works with any custom host configuration
    // TODO: Refactor VITE_API_URL to be base URL without /api suffix globally
    const apiBaseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/api$/, '')
    
    // Legacy two-script URLs (for backward compatibility)
    const scriptUrl = `${apiBaseUrl}/knowledge/widget/js/${tenantSlug}/yodel-chat-widget-latest.js`

    // Create clean config for embed code - only include widget-relevant properties
    const embedConfig = {
      // Identity
      chat_id: config.chat_id,
      
      // Basic Settings
      chat_title: config.chat_title,
      assistant_name: config.assistant_name,
      assistant_subtitle: config.assistant_subtitle,
      
      // Colors
      header_color: config.header_color,
      chat_button_color: config.chat_button_color,
      send_button_color: config.send_button_color,
      assistant_chat_bubble_color: config.assistant_chat_bubble_color,
      assistant_chat_font_color: config.assistant_chat_font_color,
      profile_chat_bubble_color: config.profile_chat_bubble_color,
      profile_chat_font_color: config.profile_chat_font_color,
      
      // Avatars
      show_avatar: config.show_avatar,
      assistant_avatar_url: config.assistant_avatar_url,
      user_avatar_url: config.user_avatar_url,
      profile_icon_url: config.profile_icon_url,
      
      // Privacy Policy
      privacy_policy: config.privacy_policy,
      
      // Features (V2)
      features: config.features,
      
      // API host for proxy endpoint (dynamic based on environment)
      api_host: apiBaseUrl
    }

    const settingsJson = JSON.stringify(embedConfig, null, 2)

    // NEW: Single-script embed URL (recommended)
    // Use shared_id for embed URL (preferred), fallback to kbId or chat_id
    const embedId = config.shared_id || kbId || config.chat_id
    const singleScript = `${apiBaseUrl}/knowledge/widget/embed/${tenantSlug}/${embedId}.js`
    
    // Single-script embed code (RECOMMENDED)
    const singleScriptCode = `<!-- Yodel Chat Widget (Single Script - Recommended) -->
<script src="${singleScript}" async></script>`

    // Two-script embed code (Legacy)
    const twoScriptCode = `<!-- Yodel Chat Widget (Two Scripts - Legacy) -->
<script>
  window.yodelChatSettings = ${settingsJson};
</script>
<script src="${scriptUrl}" async></script>`

    return { 
      embedCode: twoScriptCode,
      embedCodeSingleScript: singleScriptCode,
      widgetScriptUrl: scriptUrl,
      singleScriptUrl: singleScript
    }
  }, [config, tenantSlug, kbId])

  return { embedCode, embedCodeSingleScript, widgetScriptUrl, singleScriptUrl }
}
